import React, { useCallback } from 'react';
import { t } from '@lingui/macro';
import { AnalyticsSource, AuthenticatedActions } from 'types';
import { Sample } from 'types/generated/graphql';
import { usePrivateAction } from 'hooks/usePrivateAction';
import { useCollectionModalContext } from 'modals/CollectionModal/CollectionModalContext';
import { useSessionStorageContext } from 'contexts/SessionStorageContext/SessionStorageContext';

const useAddSampleToCollectionButton = ({
    samples,
    onClick,
    setCollectionListToPopover,
    source,
    placeholder,
    hide,
    openAddToCollection,
}: {
    samples: Sample[];
    onClick?: () => void;
    setCollectionListToPopover?: () => void;
    source: AnalyticsSource;
    placeholder?: string;
    hide?: () => void;
    openAddToCollection: (
        samples: Sample[],
        source: AnalyticsSource,
        placeholder?: string,
    ) => void;
}): {
    handleOnClick: (event?: React.MouseEvent<Element>) => void;
} => {
    const doPrivateAction = usePrivateAction();
    const { storeSamplesToAddToCollection } = useSessionStorageContext();

    const { setSamplesToAdd } = useCollectionModalContext();
    const handleOnClick = useCallback(() => {
        doPrivateAction({
            handleAuthenticated: (): void => {
                setSamplesToAdd(samples);
                openAddToCollection(samples, source, placeholder); // this is also a doPrivateAction
                onClick && onClick();
                setCollectionListToPopover && setCollectionListToPopover();
            },
            handleGuest: () => storeSamplesToAddToCollection(samples),
            actionDescription: {
                type: AuthenticatedActions.addSamplesToCollection,
                description: t`add to collection`,
                args: [],
            },
        });
    }, [
        openAddToCollection,
        samples,
        source,
        onClick,
        setCollectionListToPopover,
        hide,
        placeholder,
        setSamplesToAdd,
        doPrivateAction,
        storeSamplesToAddToCollection,
    ]);

    return { handleOnClick };
};

export default useAddSampleToCollectionButton;
